import * as turf from '@turf/turf'
import type { boundarySchema, rxSchema } from 'api-validations/geojson-schemas'
import { PlanAsset } from 'api-validations/locutus'
import type { MultiPolygon, Position } from 'geojson'
import type { z } from 'zod'

/**
 * A boundary's or prescription's geometry, whether found in an upload or stored.
 */
export type PlanAssetGeometry =
  | ({ assetType: PlanAsset.Boundary } & z.infer<typeof boundarySchema>)
  | ({ assetType: PlanAsset.Prescription } & Pick<z.infer<typeof rxSchema>, 'prescription'>)

/**
 * A closed ring of `[lon, lat]` positions.
 */
type Ring = readonly Position[]

/**
 * Outer ring first, then holes.
 */
type Rings = Position[][]

type PreviewGeometry =
  | { type: 'Polygon'; coordinates: Rings }
  | { type: 'MultiPolygon'; coordinates: Rings[] }

type PreviewFeature = {
  geometry: PreviewGeometry
  properties: Record<string, unknown> | null
}

export type PreviewPolygon = {
  rings: Rings
  /**
   * The feature this polygon came from. Useful for coloring multipolygon zones.
   */
  featureIndex: number
}

export type PlanAssetPreview = {
  bbox: readonly [number, number, number, number]
  polygons: readonly PreviewPolygon[]
  acres: number
  /**
   * Whether the area is excluded from the field rather than part of it.
   */
  inverted: boolean
}

const DEG_TO_RAD = Math.PI / 180

const polygonsOf = (geometry: PreviewGeometry): readonly Rings[] =>
  geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates

/**
 * The GeoJSON features an asset is drawn from.
 */
export const planFeatures = (asset: PlanAssetGeometry): readonly PreviewFeature[] => {
  if (asset.assetType === PlanAsset.Prescription) return asset.prescription.features
  return asset.boundary.type === 'FeatureCollection' ? asset.boundary.features : [asset.boundary]
}

/**
 * The polygons, extent, and acreage a thumbnail needs, or undefined if the asset encloses no area.
 */
export const toPlanAssetPreview = (asset: PlanAssetGeometry): PlanAssetPreview | undefined => {
  const polygons = planFeatures(asset).flatMap((feature, featureIndex) =>
    polygonsOf(feature.geometry)
      .filter((rings) => rings.length > 0)
      .map((rings) => ({ rings, featureIndex }))
  )

  if (polygons.length === 0) return undefined

  const outline: MultiPolygon = {
    type: 'MultiPolygon',
    coordinates: polygons.map(({ rings }) => rings)
  }
  const [minLon, minLat, maxLon, maxLat] = turf.bbox(outline)

  return {
    bbox: [minLon, minLat, maxLon, maxLat],
    polygons,
    acres: turf.convertArea(turf.area(outline), 'meters', 'acres'),
    inverted: asset.assetType === PlanAsset.Boundary && asset.inverted
  }
}

export type PreviewTransform = {
  /**
   * Pixels per degree of longitude.
   */
  scaleX: number
  /**
   * Pixels per degree of latitude.
   */
  scaleY: number
  offsetX: number
  offsetY: number
  minLon: number
  maxLat: number
}

/**
 * Fits an extent into a box without distorting it. A degree of longitude shrinks as it
 * moves away from the equator, so longitude is corrected by latitude before scaling.
 */
export const previewTransform = ({
  bbox,
  width,
  height,
  padding = 1
}: {
  bbox: readonly [number, number, number, number]
  width: number
  height: number
  padding?: number
}): PreviewTransform => {
  const [minLon, minLat, maxLon, maxLat] = bbox
  const lonPerLat = Math.cos(((minLat + maxLat) / 2) * DEG_TO_RAD)
  const spanX = Math.max((maxLon - minLon) * lonPerLat, Number.EPSILON)
  const spanY = Math.max(maxLat - minLat, Number.EPSILON)
  const pixelsPerLatDegree = Math.min((width - padding * 2) / spanX, (height - padding * 2) / spanY)

  return {
    scaleX: pixelsPerLatDegree * lonPerLat,
    scaleY: pixelsPerLatDegree,
    offsetX: (width - spanX * pixelsPerLatDegree) / 2,
    offsetY: (height - spanY * pixelsPerLatDegree) / 2,
    minLon,
    maxLat
  }
}

/**
 * Projects a ring to a flat `x, y, …` pixel list, skipping positions that
 * land on the previous pixel.
 */
export const projectRing = (
  ring: Ring,
  { scaleX, scaleY, offsetX, offsetY, minLon, maxLat }: PreviewTransform
) => {
  const points: number[] = []
  let lastX = Number.NaN
  let lastY = Number.NaN

  for (const [lon, lat] of ring) {
    const x = offsetX + (lon - minLon) * scaleX
    const y = offsetY + (maxLat - lat) * scaleY
    if (Math.round(x) === Math.round(lastX) && Math.round(y) === Math.round(lastY)) continue
    points.push(x, y)
    lastX = x
    lastY = y
  }

  return points
}
